import {COMPANY_KEYS} from "@/api/companyApi";
import {USER_KEYS} from "@/api/userApi";
import NotificationCountIndicator from "@/components/NavBar/NotificationBadge";
import NotificationPanel from "@/components/NavBar/NotificationPanel";
import type {CompanyMessageCountsSerializer, MessageCountsSerializer} from "@/generated/DjangoTypes";
import {useGenericQuery} from "@/hooks/useQueryHooks";
import {useSelectedCompanyId, useUserId} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import useNotificationStore from "@/store/notification";
import {Bell as BellIcon} from "lucide-react";
import {useEffect, type FC} from "react";

const Notifications: FC<{
  className?: string;
}> = ({className}) => {
  const {isOpen, toggleOpen, setValue} = useNotificationStore();
  const userId = useUserId();
  const selectedCompanyId = useSelectedCompanyId();
  const {data: userNotificationCounts} = useGenericQuery<MessageCountsSerializer>({
    queryKey: USER_KEYS.qUserNotificationCounts(userId),
    enabled: !!userId,
  });
  const {data: companyNotificationCounts} = useGenericQuery<CompanyMessageCountsSerializer>({
    queryKey: COMPANY_KEYS.qCompanyNotificationCounts(selectedCompanyId!),
    enabled: !!selectedCompanyId,
  });

  useEffect(() => {
    if (companyNotificationCounts && userNotificationCounts) {
      setValue("notificationCounts", {
        notification: userNotificationCounts.notification,
        chat: companyNotificationCounts.chat,
      });
    }
  }, [companyNotificationCounts, userNotificationCounts, setValue]);

  const notificationCount = useNotificationStore(state => state.notificationCounts.notification.unseen);

  return (
    <>
      <div className={className}>
        <button onClick={() => toggleOpen(!isOpen)} className="group flex items-center justify-center">
          <div
            className={clsxm(
              "relative flex size-10 items-center justify-center rounded-full transition-colors",
              isOpen ? "bg-neutral-white-dark" : "hover:bg-neutral-white-dark",
            )}>
            <BellIcon
              className={clsxm(
                "group-hover:text-primary-dark-solid size-4.5 transition-all duration-200",
                isOpen ? "text-primary-dark-solid" : "text-neutral-black",
              )}
            />
            <NotificationCountIndicator
              isFilled
              count={notificationCount}
              className="absolute top-0.5 right-0.5 origin-top-right border-2 border-white"
            />
          </div>
        </button>
      </div>

      <NotificationPanel isOpen={isOpen} onClose={() => toggleOpen(false)} />
    </>
  );
};

export default Notifications;
